/**
 * useDragFill.ts
 * Excel-style drag-fill handle hook for the Skill Matrix grid.
 *
 * When the user mouse-downs on the fill handle of a focused cell (that has
 * a non-null level), this hook tracks the drag, highlights a preview
 * rectangle (single axis — down or right), and on mouseup fires
 * applyAndPersist for all preview cells.
 *
 * Direction is constrained to the dominant axis:
 *   |deltaX| > |deltaY| → horizontal (fill across task types for one employee)
 *   |deltaY| >= |deltaX| → vertical   (fill down employees for one task type)
 *
 * Escape during drag cancels without applying.
 *
 * Design note: window-level mousemove/mouseup listeners are used during a drag.
 * They are always cleaned up on mouseup, Escape, and component unmount.
 * Self-referencing circular closure is avoided by storing the listener
 * functions in stable refs (listenerRefs) instead of useCallback, so
 * removeEventListener always receives the same function object.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { SkillUser, MatrixSkill, SkillMatrixCell } from '../../../../services/scheduleSkillMatrix';
import { useSkillMatrixStore } from '../../../../state/skillMatrixStore';
import type { CellCoord } from './selectionMath';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DragPreviewRect {
  startCoord: CellCoord;
  endCoord: CellCoord;
}

export interface UseDragFillOpts {
  users: SkillUser[];
  skills: MatrixSkill[];
  /** The currently-focused cell (source of the fill). */
  sourceCoord: CellCoord | null;
  /** Level of the source cell — null means no handle is rendered. */
  sourceLevel: number | null;
  /** Ref to the table container div — used to satisfy the hook signature;
   *  cells are resolved via document.elementFromPoint. */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export interface UseDragFillResult {
  isDragging: boolean;
  previewRect: DragPreviewRect | null;
  /** Attach to the FillHandle's onMouseDown. */
  onHandleMouseDown: (e: React.MouseEvent) => void;
}

// ── Exported pure helper (also unit-tested) ───────────────────────────────────

/**
 * Determine fill direction from the pixel delta since drag start.
 * Ties go to vertical (same behaviour as Excel — column fill is more common).
 */
export function computeFillDirection(
  deltaX: number,
  deltaY: number,
): 'horizontal' | 'vertical' {
  return Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useDragFill(opts: UseDragFillOpts): UseDragFillResult {
  const { users, skills, sourceCoord, sourceLevel } = opts;

  const [isDragging, setIsDragging] = useState(false);
  const [previewRect, setPreviewRect] = useState<DragPreviewRect | null>(null);

  // ── Stable data refs (avoid re-registering window listeners on every render) ─

  const startPixelRef = useRef<{ x: number; y: number } | null>(null);
  const sourceCoordRef = useRef<CellCoord | null>(null);
  const sourceLevelRef = useRef<number | null>(null);
  // Keep latest users/skills without closing over stale values.
  const usersRef = useRef<SkillUser[]>(users);
  const skillsRef = useRef<MatrixSkill[]>(skills);
  // Keep latest previewRect so the async mouseup can read it without re-registering.
  const previewRectRef = useRef<DragPreviewRect | null>(null);
  // Track dragging state in a ref for the keydown handler (avoids stale closure).
  const isDraggingRef = useRef(false);

  useEffect(() => { usersRef.current = users; }, [users]);
  useEffect(() => { skillsRef.current = skills; }, [skills]);
  useEffect(() => { previewRectRef.current = previewRect; }, [previewRect]);
  useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);

  // ── Listener refs — stable function objects for add/removeEventListener ──────
  // Using refs instead of useCallback prevents the circular dependency where
  // handleMouseUp references itself inside its own cleanup.

  const listenerRefs = useRef<{
    mousemove: ((e: MouseEvent) => void) | null;
    mouseup: (() => Promise<void>) | null;
  }>({ mousemove: null, mouseup: null });

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const resolveCellAt = useCallback(
    (clientX: number, clientY: number): CellCoord | null => {
      const el = document.elementFromPoint(clientX, clientY);
      if (!el) return null;
      const td = (el as HTMLElement).closest('[data-user-key]') as HTMLElement | null;
      if (!td) return null;
      const userKey = td.getAttribute('data-user-key');
      const skillKey = td.getAttribute('data-task-type-key');
      if (!userKey || !skillKey) return null;
      return { userKey, skillKey };
    },
    [],
  );

  const computePreviewRect = useCallback(
    (
      src: CellCoord,
      deltaX: number,
      deltaY: number,
      hoverCoord: CellCoord,
    ): DragPreviewRect | null => {
      const direction = computeFillDirection(deltaX, deltaY);
      const currentUsers = usersRef.current;
      const currentSkills = skillsRef.current;

      const srcRowIdx = currentUsers.findIndex((u) => u.AppUserKey === src.userKey);
      const srcColIdx = currentSkills.findIndex((tt) => tt.SkillKey === src.skillKey);
      if (srcRowIdx === -1 || srcColIdx === -1) return null;

      const hoverRowIdx = currentUsers.findIndex((u) => u.AppUserKey === hoverCoord.userKey);
      const hoverColIdx = currentSkills.findIndex(
        (tt) => tt.SkillKey === hoverCoord.skillKey,
      );
      if (hoverRowIdx === -1 || hoverColIdx === -1) return null;

      if (direction === 'vertical') {
        // Lock column, span rows from source to hover.
        const minRow = Math.min(srcRowIdx, hoverRowIdx);
        const maxRow = Math.max(srcRowIdx, hoverRowIdx);
        return {
          startCoord: {
            userKey: currentUsers[minRow].AppUserKey,
            skillKey: src.skillKey,
          },
          endCoord: {
            userKey: currentUsers[maxRow].AppUserKey,
            skillKey: src.skillKey,
          },
        };
      } else {
        // Lock row, span columns from source to hover.
        const minCol = Math.min(srcColIdx, hoverColIdx);
        const maxCol = Math.max(srcColIdx, hoverColIdx);
        return {
          startCoord: {
            userKey: src.userKey,
            skillKey: currentSkills[minCol].SkillKey,
          },
          endCoord: {
            userKey: src.userKey,
            skillKey: currentSkills[maxCol].SkillKey,
          },
        };
      }
    },
    [],
  );

  const coordsFromRect = useCallback((rect: DragPreviewRect): CellCoord[] => {
    const currentUsers = usersRef.current;
    const currentSkills = skillsRef.current;

    const startRowIdx = currentUsers.findIndex(
      (u) => u.AppUserKey === rect.startCoord.userKey,
    );
    const endRowIdx = currentUsers.findIndex(
      (u) => u.AppUserKey === rect.endCoord.userKey,
    );
    const startColIdx = currentSkills.findIndex(
      (tt) => tt.SkillKey === rect.startCoord.skillKey,
    );
    const endColIdx = currentSkills.findIndex(
      (tt) => tt.SkillKey === rect.endCoord.skillKey,
    );

    if (startRowIdx === -1 || endRowIdx === -1 || startColIdx === -1 || endColIdx === -1) {
      return [];
    }

    const result: CellCoord[] = [];
    for (let r = startRowIdx; r <= endRowIdx; r++) {
      for (let c = startColIdx; c <= endColIdx; c++) {
        result.push({
          userKey: currentUsers[r].AppUserKey,
          skillKey: currentSkills[c].SkillKey,
        });
      }
    }
    return result;
  }, []);

  // ── Teardown helper — removes listeners and resets drag state ─────────────────

  const stopDrag = useCallback((applyFill: boolean) => {
    if (listenerRefs.current.mousemove) {
      window.removeEventListener('mousemove', listenerRefs.current.mousemove);
    }
    if (listenerRefs.current.mouseup) {
      window.removeEventListener('mouseup', listenerRefs.current.mouseup);
    }
    listenerRefs.current.mousemove = null;

    setIsDragging(false);
    isDraggingRef.current = false;

    if (!applyFill) {
      setPreviewRect(null);
      previewRectRef.current = null;
      startPixelRef.current = null;
      sourceCoordRef.current = null;
      sourceLevelRef.current = null;
    }
  }, []);

  // ── Register stable window listeners and store them in listenerRefs ───────────

  const registerListeners = useCallback(() => {
    const onMouseMove = (e: MouseEvent): void => {
      const src = sourceCoordRef.current;
      const startPx = startPixelRef.current;
      if (!src || !startPx) return;

      const deltaX = e.clientX - startPx.x;
      const deltaY = e.clientY - startPx.y;

      const hoverCoord = resolveCellAt(e.clientX, e.clientY);
      if (!hoverCoord) return;

      const rect = computePreviewRect(src, deltaX, deltaY, hoverCoord);
      setPreviewRect(rect);
    };

    const onMouseUp = async (): Promise<void> => {
      const src = sourceCoordRef.current;
      const level = sourceLevelRef.current;
      const rect = previewRectRef.current;

      // Unregister listeners before the async work.
      stopDrag(false);

      if (!src || level === null || !rect) return;

      const coords = coordsFromRect(rect);
      if (coords.length === 0) return;

      const { applyAndPersist, cells: currentCells } = useSkillMatrixStore.getState();

      const batchPayload = coords.map((coord) => ({
        UserKey: coord.userKey,
        SkillKey: coord.skillKey,
        Level: level,
      }));

      // Build fallback cells for coords not yet in local state.
      const fallbacks: SkillMatrixCell[] = [];
      for (const coord of coords) {
        const existing = currentCells.find(
          (c) => c.UserKey === coord.userKey && c.SkillKey === coord.skillKey,
        );
        if (!existing) {
          const user = usersRef.current.find((u) => u.AppUserKey === coord.userKey);
          const tt = skillsRef.current.find((t) => t.SkillKey === coord.skillKey);
          if (user && tt) {
            fallbacks.push({
              SkillMatrixKey: null,
              UserKey: coord.userKey,
              UserDisplayName: user.DisplayName,
              UserEmail: user.Email,
              JobClass: user.JobClass,
              WorkGroup: user.WorkGroup,
              Tier: user.Tier,
              SkillKey: coord.skillKey,
              SkillName: tt.Name,
              SkillCategory: tt.Category,
              Level: null,
            });
          }
        }
      }

      try {
        await applyAndPersist(batchPayload, fallbacks);
      } catch {
        // The store reverts on failure. The page-level toast is not accessible
        // here, so the user sees the optimistic update revert silently.
        // Acceptable: page-level toasts already cover this via global error handling.
      }
    };

    listenerRefs.current.mousemove = onMouseMove;
    listenerRefs.current.mouseup = onMouseUp;

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [resolveCellAt, computePreviewRect, coordsFromRect, stopDrag]);

  // ── Esc key listener ─────────────────────────────────────────────────────────

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isDraggingRef.current) {
        stopDrag(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [stopDrag]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      stopDrag(false);
    };
  }, [stopDrag]);

  // ── Handle mousedown on the fill handle ──────────────────────────────────────

  const onHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (sourceLevel === null || !sourceCoord) return;
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation(); // Do not trigger the cell's own mousedown (range selection).

      startPixelRef.current = { x: e.clientX, y: e.clientY };
      sourceCoordRef.current = sourceCoord;
      sourceLevelRef.current = sourceLevel;

      const initial: DragPreviewRect = { startCoord: sourceCoord, endCoord: sourceCoord };
      setPreviewRect(initial);
      previewRectRef.current = initial;

      setIsDragging(true);
      isDraggingRef.current = true;

      registerListeners();
    },
    [sourceCoord, sourceLevel, registerListeners],
  );

  return { isDragging, previewRect, onHandleMouseDown };
}
